import {
  Button,
  CloseButton,
  Flex,
  ModalBody,
  ModalContent,
  ModalHeader,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import { useBoolean } from '@chakra-ui/react';
import { Editor } from '@tiptap/react';
import {
  DESIGNAI_TEXT_ADDED,
  DESIGNAI_TEXT_GENERATED,
  DESIGNAI_TEXT_GENERATED_ERROR,
  DESIGNAI_TEXT_GENERATION_DISCARDED,
  DESIGNAI_TEXT_GENERATION_STOPPED,
  DESIGNAI_TEXT_REPLACED,
  MAX_MIXPANEL_STRING_CHARS,
} from 'constants/mixpanel';
import { useRelativeWidgetPosition } from 'hooks/useRelativeWidgetPosition';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useE2Client } from 'modules/E2ClientProvider';
import { addNewWidget, useAppDispatch } from 'modules/Editor/store';
import { UserType, useUserGenerativeLimit } from 'modules/User';
import { GenerationDiscardTrigger } from 'modules/common/components/GenerationDiscardModal';
import { useToast } from 'modules/common/components/Toast';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customStyles } from 'theme/custom';
import { useWidget } from 'widgets/sdk';

import { GenerationUpgradeBanner } from '../../GenerationUpgradeBanner';
import { GeneratedText } from './GeneratedText';
import {
  DEFAULT_BUTTON_PROPS,
  GENERATE_TEXT_TERMINATOR,
  MODAL_THRESHOLD,
  MODAL_WIDTH,
  START_MODAL_HEIGHT,
} from './ImproveText.config';
import { GENERATION_DISCARD_TEXT } from './ImproveText.config';
import { generateImprovedTextWidgetData } from './ImproveText.helpers';
import { GenerationState } from './ImproveText.types';
import { UserInput } from './UserInput';

interface GenerativeTextCreditsProps {
  count: number;
  isRateLimited: boolean;
  max: number;
}
interface ImproveTextModalContentProps {
  editor?: Editor | null;
  isOpen: boolean;
  onClose: (forceClose?: boolean) => void;
  onGenerateText?: () => void;
  onDiscardText?: () => void;
  onReplaceTextContent: (newTextContent: string) => void;
  onOpenUpgradeModal: () => void; // To open up upgrade modal
  isGatedUser: boolean; // To check whether users are Free or premium
  userId: string | undefined;
  userType: UserType;
  generativeTextCredits: GenerativeTextCreditsProps | undefined;
  showGeneratedTextCredits: boolean;
}

export const ImproveTextModalContent = memo(
  ({
    editor,
    isOpen,
    onClose,
    onReplaceTextContent,
    onDiscardText,
    onGenerateText,
    onOpenUpgradeModal,
    isGatedUser,
    userId,
    userType,
    generativeTextCredits,
    showGeneratedTextCredits,
  }: ImproveTextModalContentProps) => {
    const { t } = useTranslation('editor_widget_toolbar', {
      useSuspense: false,
      keyPrefix: 'textToolbar.improveText.modal',
    });
    const toast = useToast();

    const modalContentRef = useRef<HTMLDivElement>(null);

    // State to indicate the process of generating text
    // Idle - no generation in progress
    // Loading - generation was triggered and waiting for response from server
    // Generating - reading the streaming response from the server
    const [generationState, setGenerationState] = useState<GenerationState>(GenerationState.Idle);

    // Flag to indicate if text has been generated at least once
    // FALSE, if current text is the same as the original content of the text widget
    const [hasGeneratedText, setHasGeneratedText] = useBoolean(false);

    // Flag to indicate if user hit the "Stop generating" button
    const shouldStopGenerating = useRef(false);

    // Call GET API after submission
    const { refetch } = useUserGenerativeLimit(userId);

    // check user is rate limit?
    const isUserRateLimit = generativeTextCredits !== undefined && generativeTextCredits.isRateLimited === true;

    // Whether to show available credits in modal (for prem)
    const showCredits = generativeTextCredits !== undefined && showGeneratedTextCredits;

    // Generated text to be displayed in the modal
    const [text, setText] = useState('');

    // Disable all buttons while generating text
    const isDisabled = isUserRateLimit || generationState !== GenerationState.Idle;
    const isGenerating = generationState === GenerationState.Generating;

    // Text to be passed to the server for generation - doesn't need to be stored in state
    const textRef = useRef('');

    const { client } = useE2Client();
    const { widgetId } = useWidget();
    const dispatch = useAppDispatch();

    const { resetElementToStartPosition: resetModalToStartPosition } = useRelativeWidgetPosition({
      widgetId,
      elementToPositionRef: modalContentRef,
      placement: 'bottom',
      threshold: MODAL_THRESHOLD as [number, number],
      startDimension: { width: MODAL_WIDTH, height: START_MODAL_HEIGHT },
      scrollIntoViewOptions: { block: 'nearest', inline: 'center' },
    });

    // Update state and ref when text changes
    const updateText = useCallback((newText: string) => {
      setText(newText);
      textRef.current = newText;
    }, []);

    // Reset text when modal is opened
    useEffect(() => {
      if (!isOpen) return;
      textRef.current = editor?.getText() || '';
    }, [isOpen, editor]);

    // If modal is about to overflow the page, set the bottom to 0
    const adjustPosition = useCallback(({ height }: { height?: number }) => {
      if (!modalContentRef.current) return;

      // Don't adjust if already adjusted
      if (modalContentRef.current.style.bottom !== '') return;

      const top = parseFloat(modalContentRef.current.style.top) ?? 0;
      const remainingHeight = window.innerHeight - top;
      if (height && height > remainingHeight) {
        modalContentRef.current.style.bottom = '0px';
        modalContentRef.current.style.top = 'unset';
      }
    }, []);

    useResizeObserver({ ref: modalContentRef, onResize: adjustPosition });

    // Trigger streaming response and update text as data comes in
    const onImproveText = useCallback(
      async (instructions: string) => {
        // If Rate Limited, Pop out Upgrade Modal
        if (isUserRateLimit) return onOpenUpgradeModal();

        setGenerationState(GenerationState.Loading);
        try {
          // if its not Gated User dont need to update
          const generatedTextReader = await client?.generateText(
            textRef.current,
            instructions,
            isGatedUser ? userId : undefined,
          );
          if (!generatedTextReader) {
            throw new Error('Failed to retrieve streaming response.');
          }

          let generatedText = '';
          let finishedReading = false;

          updateText('');
          setHasGeneratedText.on();
          setGenerationState(GenerationState.Generating);
          while (!shouldStopGenerating.current) {
            const { value, done } = await generatedTextReader.read();
            if (done) {
              finishedReading = true;
              break;
            }

            // Retrieve data from streaming response and update text
            // Generated text stored in JSON format: { text: "generated text" }
            // GENERATE_TEXT_TERMINATOR is sent when generation is complete and there is no more text.
            const deltaTextJson = new TextDecoder().decode(value).trim().split('\n');
            for (const json of deltaTextJson) {
              if (!json) continue;
              if (json === GENERATE_TEXT_TERMINATOR) break;

              const deltaText = JSON.parse(json.trim().replace(/\n/g, ''))?.text || '';
              if (deltaText) {
                generatedText += deltaText;
              }
            }
            updateText(generatedText);
          }

          // Cancel request if user clicks the stop generating button before entire response is read
          if (!finishedReading) {
            generatedTextReader.cancel();

            Mixpanel.track(DESIGNAI_TEXT_GENERATION_STOPPED.eventName);
          }

          Mixpanel.track(DESIGNAI_TEXT_GENERATED.eventName, {
            prompt: instructions,
            generated_text: generatedText.substring(0, MAX_MIXPANEL_STRING_CHARS),
          });

          onGenerateText?.();
        } catch (err) {
          toast({
            status: 'error',
            title: t('generationError'),
          });

          Mixpanel.track(DESIGNAI_TEXT_GENERATED_ERROR.eventName);
        } finally {
          setGenerationState(GenerationState.Idle);
          shouldStopGenerating.current = false;

          // Only refetch for gated users
          if (isGatedUser) {
            await refetch(); // refetch after submission
          }
        }
      },
      // Safe to not include setHasGeneratedText from dependencies list because it's a setter
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [toast, client, onGenerateText, updateText],
    );

    const onStopGenerating = () => {
      shouldStopGenerating.current = true;
    };

    // Click "Replace text" button
    const onReplace = () => {
      if (!editor || !text) return;

      onReplaceTextContent(text);

      // Close modal, skip discard modal
      onClose(true);

      toast({ title: t('replaceTextToast') });
      Mixpanel.track(DESIGNAI_TEXT_REPLACED.eventName);
    };

    // Click "Add new text" button
    // Will create a new text widget below the current widget
    const onAdd = () => {
      if (!editor) return;

      const newWidgetData = generateImprovedTextWidgetData(editor, text, widgetId);

      if (!newWidgetData) return;
      dispatch(addNewWidget(newWidgetData));

      // Close modal, skip discard modal
      onClose(true);

      toast({ title: t('addTextToast') });
      Mixpanel.track(DESIGNAI_TEXT_ADDED.eventName);
    };

    // Reset to the original text in the widget, discard generation.
    const onDiscardGeneration = () => {
      updateText(editor?.getText() || '');
      setHasGeneratedText.off();
      onDiscardText?.();

      resetModalToStartPosition();

      Mixpanel.track(DESIGNAI_TEXT_GENERATION_DISCARDED.eventName);
    };

    const handleClose = () => onClose();

    return (
      <ModalContent
        position='absolute'
        mt={4}
        mb={5}
        ref={modalContentRef}
        borderLeft={customStyles.container.violetGradient.borderLeft}
        background={customStyles.container.violetGradient.background}
        h='fit-content'
      >
        <VisuallyHidden>
          <ModalHeader>{t('header')}</ModalHeader>
        </VisuallyHidden>

        {showCredits ? (
          <Text position='absolute' size={'xs'} top={4} left={4} fontSize={'xs'}>
            {generativeTextCredits.count}/{generativeTextCredits.max} {t('credits')}
          </Text>
        ) : null}
        {(showCredits || hasGeneratedText) && (
          <CloseButton position='absolute' top={2} right={3} onClick={handleClose} />
        )}
        <ModalBody p={4} mt={showCredits || hasGeneratedText ? 6 : 0}>
          <Flex direction='column' gap={2}>
            {hasGeneratedText && (
              <Flex
                flexDir='column'
                flex='1'
                p={4}
                bg='violet.50'
                mb={!isUserRateLimit ? 4 : 0}
                borderRadius='base'
                fontSize='sm'
                fontWeight='normal'
                overflow='hidden'
                minH={0}
                h='fit-content'
              >
                <GeneratedText text={text} isGenerating={isGenerating} />
                {/* STOP GENERATING BUTTON */}
                {isGenerating && (
                  <Flex justifyContent='flex-end' mt={4}>
                    <Button
                      {...DEFAULT_BUTTON_PROPS}
                      onClick={onStopGenerating}
                      variant='outline'
                      borderColor='gray.200'
                    >
                      {t('stopGenerating')}
                    </Button>
                  </Flex>
                )}
                {/* DISCARD + ADD + REPLACE BUTTONS */}
                {(!isDisabled || text !== '') && (
                  <Flex justifyContent='space-between' mt={4} h='28px'>
                    <GenerationDiscardTrigger onDiscard={onDiscardGeneration} fromParent={GENERATION_DISCARD_TEXT} />
                    <Flex gap={2}>
                      <Button {...DEFAULT_BUTTON_PROPS} onClick={onAdd} variant='ghost'>
                        {t('addText')}
                      </Button>
                      <Button {...DEFAULT_BUTTON_PROPS} onClick={onReplace} colorScheme='violet'>
                        {t('replaceText')}
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Flex>
            )}
            {isUserRateLimit && <GenerationUpgradeBanner userType={userType} />}
            <UserInput onSubmit={onImproveText} isDisabled={isDisabled} />
          </Flex>
        </ModalBody>
      </ModalContent>
    );
  },
);

ImproveTextModalContent.displayName = 'TextGenerationModalContent';
